import type { HeightFrame, UnitProfile } from "../types";

const FEET_PER_METER = 3.280839895;
const KMH_PER_KNOT = 1.852;
const FPM_PER_MS = 196.850393701;
const METERS_PER_1000_FEET = 304.8;

function trimTrailingZeroes(value: string): string {
  return value.replace(/\.0+$|(\.\d*[1-9])0+$/, "$1");
}

function formatFinite(value: number, digits: number, locale?: string): string {
  if (!locale) return trimTrailingZeroes(value.toFixed(digits));
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
    useGrouping: false
  }).format(value);
}

export function feetToMeters(feet: number): number {
  return feet / FEET_PER_METER;
}

export function metersToFeet(meters: number): number {
  return meters * FEET_PER_METER;
}

export function knotsToKmh(knots: number): number {
  return knots * KMH_PER_KNOT;
}

export function kmhToKnots(kmh: number): number {
  return kmh / KMH_PER_KNOT;
}

export function fahrenheitToCelsius(fahrenheit: number): number {
  return (fahrenheit - 32) * (5 / 9);
}

export function metresPerSecondToFpm(value: number): number {
  return value * FPM_PER_MS;
}

export function convertHeightFromFeet(
  valueFeet: number,
  units: UnitProfile
): number {
  return units === "metric" ? feetToMeters(valueFeet) : valueFeet;
}

export function convertSpeedFromKnots(
  valueKnots: number,
  units: UnitProfile
): number {
  return units === "metric" ? knotsToKmh(valueKnots) : valueKnots;
}

export function convertTemperatureFromFahrenheit(
  valueFahrenheit: number,
  units: UnitProfile
): number {
  return units === "metric" ? fahrenheitToCelsius(valueFahrenheit) : valueFahrenheit;
}

export function heightUnitLabel(
  units: UnitProfile,
  frame?: HeightFrame
): string {
  if (units === "metric") {
    if (frame === "altitude_msl_ft") return "m MSL";
    if (frame === "altitude_model_agl_ft") return "m model AGL";
    if (frame === "altitude_launch_agl_ft") return "m launch AGL";
    return "m";
  }
  if (frame === "altitude_msl_ft") return "ft MSL";
  if (frame === "altitude_model_agl_ft") return "ft model AGL";
  if (frame === "altitude_launch_agl_ft") return "ft launch AGL";
  return "ft";
}

export function speedUnitLabel(units: UnitProfile): string {
  return units === "metric" ? "km/h" : "kt";
}

export function temperatureUnitLabel(units: UnitProfile): string {
  return units === "metric" ? "°C" : "°F";
}

export function formatHeight(
  valueFeet: number | null | undefined,
  units: UnitProfile,
  digits = 0,
  locale?: string
): string {
  if (valueFeet === null || valueFeet === undefined || !Number.isFinite(valueFeet)) {
    return "—";
  }
  return formatFinite(convertHeightFromFeet(valueFeet, units), digits, locale);
}

export function formatSpeed(
  valueKnots: number | null | undefined,
  units: UnitProfile,
  digits = 1,
  locale?: string
): string {
  if (
    valueKnots === null ||
    valueKnots === undefined ||
    !Number.isFinite(valueKnots)
  ) {
    return "—";
  }
  return formatFinite(convertSpeedFromKnots(valueKnots, units), digits, locale);
}

export function formatTemperature(
  valueFahrenheit: number | null | undefined,
  units: UnitProfile,
  digits = 0,
  locale?: string
): string {
  if (
    valueFahrenheit === null ||
    valueFahrenheit === undefined ||
    !Number.isFinite(valueFahrenheit)
  ) {
    return "—";
  }
  return formatFinite(convertTemperatureFromFahrenheit(valueFahrenheit, units), digits, locale);
}

export function formatInputAirspeed(
  valueKnots: number | undefined,
  units: UnitProfile
): string {
  if (valueKnots === undefined || !Number.isFinite(valueKnots)) {
    return "";
  }
  return formatFinite(convertSpeedFromKnots(valueKnots, units), 1);
}

export function parseInputAirspeed(
  value: string,
  units: UnitProfile
): number | undefined {
  if (value.trim() === "") return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return units === "metric" ? kmhToKnots(parsed) : parsed;
}

export function formatInputCruiseAltitude(
  valueFeet: number | undefined,
  units: UnitProfile
): string {
  if (valueFeet === undefined || !Number.isFinite(valueFeet)) {
    return "";
  }
  return formatFinite(convertHeightFromFeet(valueFeet, units), 0);
}

export function parseInputCruiseAltitude(
  value: string,
  units: UnitProfile
): number | undefined {
  if (value.trim() === "") return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return units === "metric" ? metersToFeet(parsed) : parsed;
}

export function formatSecondaryFpm(
  valueMs: number | null | undefined,
  locale?: string
): string {
  if (valueMs === null || valueMs === undefined || !Number.isFinite(valueMs)) {
    return "—";
  }
  return Math.round(metresPerSecondToFpm(valueMs)).toLocaleString(locale);
}

export function convertStabilityRateFromCPer1000Ft(
  valueCPer1000Ft: number,
  units: UnitProfile
): number {
  if (units === "metric") {
    return (valueCPer1000Ft * 1000) / METERS_PER_1000_FEET;
  }
  return valueCPer1000Ft * (9 / 5);
}

export function stabilityRateUnitLabel(units: UnitProfile): string {
  return units === "metric" ? "°C/km" : "°F/1000 ft";
}

export function formatStabilityRate(
  valueCPer1000Ft: number | null | undefined,
  units: UnitProfile,
  digits = 2,
  locale?: string
): string {
  if (
    valueCPer1000Ft === null ||
    valueCPer1000Ft === undefined ||
    !Number.isFinite(valueCPer1000Ft)
  ) {
    return "—";
  }
  return formatFinite(convertStabilityRateFromCPer1000Ft(valueCPer1000Ft, units), digits, locale);
}
